import { useCallback, useEffect, useState } from 'react';
import SearchInput from '@components/common/SearchInput';
import PageHeader from '@components/common/PageHeader';
import { api } from '@/api/weather/log/api.ts';
import {
    WeatherScheduleLogListItem,
    WeatherScheduleLogListRes,
    WeatherScheduleLogListReq,
} from '@/api/weather/log/type.ts';
import DataTable, {
    ColumnConfig,
    HeaderButton,
} from '@components/common/DataTable/DataTable.tsx';
import DateRangePicker from '@components/common/DateRangePicker';
import { format } from 'date-fns';
import { Alert } from '@utils/alert';

const WeatherScheduleLogList = () => {
    const [filters, setFilters] = useState<WeatherScheduleLogListReq>(() => {
        return {
            pageSize: 10,
            nowPage: 1,
            pageGroup: 10,
            keyword: '',
            status: undefined,
            startDt: undefined,
            endDt: '',
        };
    });

    const updateFilter = (key: keyof WeatherScheduleLogListReq, value: any) => {
        setFilters(prev => ({
            ...prev,
            [key]: value,
            ...(key !== 'nowPage' && { nowPage: 1 }),
        }));
    };

    const handleDateChange = (dates: [Date | null, Date | null]) => {
        const [start, end] = dates;

        setFilters(prev => ({
            ...prev,
            startDt: start ? format(start, 'yyyy-MM-dd') : undefined,
            endDt: end ? format(end, 'yyyy-MM-dd') : undefined,
            nowPage: 1,
        }));
    };

    const handleSearch = (value: string) => {
        updateFilter('keyword', value);
    };

    const handlePageChange = (page: number) => {
        updateFilter('nowPage', page);
    };

    const handlePageSizeChange = (pageSize: number) => {
        updateFilter('pageSize', pageSize);
    };

    const [logRes, setLogRes] = useState<WeatherScheduleLogListRes>();

    const fetchLogs = useCallback(async () => {
        const response = await api.weatherScheduleLogList(filters);
        setLogRes(response);
    }, [filters]);

    const getStatusBadge = (status: number) => {
        const statusMap: Record<number, { text: string; className: string }> = {
            0: { text: '대기', className: 'badge bg-secondary' },
            1: { text: '시작', className: 'badge bg-info' },
            2: { text: '완료', className: 'badge bg-success' },
            3: { text: '실패', className: 'badge bg-danger' },
        };
        const statusInfo = statusMap[status] || {
            text: '알 수 없음',
            className: 'badge bg-light',
        };
        return <span className={statusInfo.className}>{statusInfo.text}</span>;
    };

    const columns: ColumnConfig<WeatherScheduleLogListItem>[] = [
        { key: 'wsIdx', header: 'No', width: '5%' },
        { key: 'areas', header: '대상지역', width: '15%' },
        {
            key: 'updateDesc',
            header: '갱신내용',
            render: value => (
                <span style={{ whiteSpace: 'pre-line' }}>{value}</span>
            ),
            width: '20%',
        },
        {
            key: 'status',
            header: '상태',
            render: value => getStatusBadge(value),
            width: '5%',
        },
        {
            key: 'errorMsg',
            header: '오류메시지',
            render: value => (
                <span style={{ whiteSpace: 'pre-line' }}>{value || '-'}</span>
            ),
            width: '15%',
        },
        { key: 'regDt', header: '등록일자', width: '7%' },
        { key: 'updDt', header: '갱신일자', width: '7%' },
        {
            key: 'regName',
            header: '등록자명',
            render: value => value || '-',
            width: '7%',
        },
    ];

    const headerButtons: HeaderButton[] = [
        {
            label: '기상데이터 갱신',
            icon: 'ri-refresh-line',
            className: 'btn btn-sm btn-primary btn-wave',
            onClick: async () => {
                await Alert.confirmAndExecute(
                    `기상데이터를 갱신하시겠습니까?`,
                    '최신기상 데이터를 불러옵니다.',
                    true,
                    async () => {
                        await api.updateWeather().then(() => {
                            fetchLogs(); // 갱신
                        });
                    },
                );
            },
        },
    ];

    useEffect(() => {
        fetchLogs();
    }, [fetchLogs]);

    return (
        <>
            <PageHeader
                title="기상 스케줄 로그"
                breadcrumbs={[
                    { label: '홈', href: '/' },
                    { label: '기상관리' },
                    { label: '기상 스케줄 로그', active: true },
                ]}
            />

            <div className="row mt-4">
                <div className="col-xl-12">
                    <div className="card custom-card">
                        <div className="card-body">
                            <div className="row g-3">
                                <div className="col-md-3">
                                    <label className="form-label">상태</label>
                                    <select
                                        className="form-select"
                                        value={filters.status || ''}
                                        onChange={e =>
                                            updateFilter(
                                                'status',
                                                e.target.value,
                                            )
                                        }
                                    >
                                        <option value="">전체</option>
                                        <option value="0">대기</option>
                                        <option value="1">시작</option>
                                        <option value="2">완료</option>
                                        <option value="3">실패</option>
                                    </select>
                                </div>
                                <div className="col-md-3">
                                    <label className="form-label">기간</label>
                                    <DateRangePicker
                                        startDate={
                                            filters.startDt
                                                ? new Date(filters.startDt)
                                                : null
                                        }
                                        endDate={
                                            filters.endDt
                                                ? new Date(filters.endDt)
                                                : null
                                        }
                                        onChange={handleDateChange}
                                        placeholder="날짜 범위를 선택하세요"
                                    />
                                </div>
                                <div className="col-md-6">
                                    <label className="form-label">검색</label>
                                    <SearchInput
                                        placeholder="시도, 구군, 동읍면"
                                        value={filters.keyword || ''}
                                        onSearch={handleSearch}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <div className="row">
                <div className="col-xl-12">
                    {logRes && (
                        <DataTable<WeatherScheduleLogListItem>
                            data={logRes?.data}
                            columns={columns}
                            headerButtons={headerButtons}
                            paging={logRes?.paging}
                            onPageChange={handlePageChange}
                            onPageSizeChange={handlePageSizeChange}
                        />
                    )}
                </div>
            </div>
        </>
    );
};

export default WeatherScheduleLogList;
